import API from '../config/api';
import React, { useState, useEffect } from 'react';

import axios from 'axios';

import { toast } from 'react-toastify';

import {
  Container,
  Row,
  Col,
  Card,
  Accordion,
  Form,
  Button
} from 'react-bootstrap';

import CustomNavbar from '../components/CustomNavbar';
import DivineSelect from '../components/DivineSelect';

const CATEGORY_OPTIONS = [
  { value: 'Order Issue', label: 'Order Issue', icon: '📦' },
  { value: 'Payment Issue', label: 'Payment Issue', icon: '💳' },
  { value: 'Shipping Issue', label: 'Shipping Issue', icon: '🚚' },
  { value: 'Refund Issue', label: 'Refund Issue', icon: '🔄' },
  { value: 'Account Issue', label: 'Account Issue', icon: '🔐' }
];

/* The same product can appear under several orders, so the option value has to
   carry the order too — keying on productId alone picks the wrong order. */
const purchaseKey = (item) => `${item.orderId}::${item.productId}`;

/* Support channels drifting through the page backdrop */
const HELP_DRIFT = [
  { char: '💬', left: '8%', size: '1.7rem', delay: '0s', duration: '27s' },
  { char: '✉️', left: '21%', size: '1.4rem', delay: '8s', duration: '33s' },
  { char: '🎧', left: '34%', size: '1.6rem', delay: '15s', duration: '29s' },
  { char: '🛟', left: '47%', size: '1.5rem', delay: '4s', duration: '36s' },
  { char: '❓', left: '60%', size: '1.3rem', delay: '21s', duration: '31s' },
  { char: '📞', left: '73%', size: '1.5rem', delay: '11s', duration: '34s' },
  { char: '💬', left: '86%', size: '1.4rem', delay: '25s', duration: '28s' },
  { char: '✨', left: '95%', size: '1.2rem', delay: '17s', duration: '37s' }
];

/* Deterministic scatter so the sparks don't reshuffle between renders */
const HELP_SPARKS = Array.from({ length: 16 }, (_, i) => ({
  left: `${((i * 43) % 95) + 3}%`,
  top: `${((i * 61) % 88) + 5}%`,
  size: `${2 + (i % 3)}px`,
  delay: `${(i * 0.55).toFixed(2)}s`,
  duration: `${(3 + (i % 5) * 0.8).toFixed(2)}s`
}));

/* Fixed, full-viewport animated backdrop. Sits at z-index -1 so it paints
   behind the content and unmounts with the page. Transform/opacity only. */
function HelpBackdrop() {
  return (
    <div className="help-bg" aria-hidden="true">
      <span className="help-bg-veil" />
      <span className="help-bg-blob help-bg-blob-1" />
      <span className="help-bg-blob help-bg-blob-2" />

      {/* Concentric "signal" arcs, like a support line broadcasting */}
      <span className="help-bg-signal">
        {[0, 1, 2].map((n) => (
          <span className="help-bg-arc" key={n} />
        ))}
      </span>

      {HELP_SPARKS.map((spark, i) => (
        <span
          key={`spark-${i}`}
          className="help-bg-spark"
          style={{
            left: spark.left,
            top: spark.top,
            width: spark.size,
            height: spark.size,
            animationDelay: spark.delay,
            animationDuration: spark.duration
          }}
        />
      ))}

      {HELP_DRIFT.map((item, i) => (
        <span
          key={`drift-${i}`}
          /* every other one is dropped on phones — see .help-bg-drift-extra */
          className={`help-bg-drift${i % 2 ? ' help-bg-drift-extra' : ''}`}
          style={{
            left: item.left,
            fontSize: item.size,
            animationDelay: item.delay,
            animationDuration: item.duration
          }}
        >
          {item.char}
        </span>
      ))}
    </div>
  );
}

function HelpPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    category: '',
    subject: '',
    message: ''
  });
  const [purchasedProducts, setPurchasedProducts] = useState([]);

  const [selectedProduct, setSelectedProduct] = useState('');

  const changeHandler = (e) => {
    setFormData({
      ...formData,

      [e.target.name]: e.target.value
    });
  };

  const submitHandler = async (e) => {
    e.preventDefault();

    /* DivineSelect is not a native control, so `required` no longer guards
       these two — check them here instead. */
    if (!formData.category) {
      toast.error('Please choose an issue category');

      return;
    }

    if (!selectedProduct) {
      toast.error('Please select the product your issue is about');

      return;
    }

    try {
      const product = purchasedProducts.find(
        (item) => purchaseKey(item) === selectedProduct
      );
      await axios.post(
        `${API}/tickets`,

        {
          ...formData,

          productId: product?.productId || '',

          productName: product?.name || '',

          orderId: product?.orderId || ''
        }
      );

      toast.success('Support Ticket Submitted Successfully');

      setFormData({
        name: '',
        email: '',
        category: '',
        subject: '',
        message: ''
      });

      setSelectedProduct('');
    } catch (error) {
      console.log(error);

      toast.error('Failed To Submit Ticket');
    }
  };
  useEffect(() => {
    const fetchPurchasedProducts = async () => {
      try {
        const userInfo = JSON.parse(localStorage.getItem('userInfo'));

        if (!userInfo) return;

        const { data } = await axios.get(
          `${API}/orders/purchased/${userInfo._id}`
        );

        setPurchasedProducts(data);
      } catch (error) {
        console.log(error);
      }
    };

    fetchPurchasedProducts();
  }, []);

  /* Sections fade up as they scroll into view. Each element is unobserved
     once it has played, so scrolling back up doesn't replay it. */
  useEffect(() => {
    const nodes = Array.from(document.querySelectorAll('.help-reveal'));

    if (!('IntersectionObserver' in window)) {
      nodes.forEach((node) => node.classList.add('is-in'));

      return undefined;
    }

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-in');
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -40px 0px' }
    );

    nodes.forEach((node) => io.observe(node));

    return () => io.disconnect();
  }, []);

  return (
    <>
      <HelpBackdrop />

      <CustomNavbar />

      {/* Hero Section — a detached card, so it reads as its own panel
          rather than an extension of the navbar */}

      <div className="help-hero-wrap">
        <Container>
          <div className="help-hero">
            <span className="help-hero-aurora" aria-hidden="true" />
            <span className="help-blob help-blob-1" />
            <span className="help-blob help-blob-2" />

            {/* Live support line — a voice waveform along the base */}
            <span className="help-wave" aria-hidden="true">
              {Array.from({ length: 34 }).map((_, i) => (
                <span
                  className="help-wave-bar"
                  key={i}
                  style={{
                    /* irrational step keeps neighbouring bars out of sync */
                    animationDelay: `${((i * 0.37) % 1.4).toFixed(2)}s`,
                    animationDuration: `${(1.3 + ((i * 0.19) % 0.7)).toFixed(2)}s`
                  }}
                />
              ))}
            </span>

            <div className="position-relative help-hero-inner">
              <div className="text-center text-white py-2">
                <span className="help-hero-badge">
                  <span className="help-hero-dot" /> We&apos;re online now
                  <span className="help-typing" aria-hidden="true">
                    <i />
                    <i />
                    <i />
                  </span>
                </span>

                <h1 className="fw-bold help-hero-title">
                  {/* The icon flips through the ways you can reach us */}
                  <span className="help-hero-icon">
                    <span className="hhi-face">🛟</span>
                    <span className="hhi-face">🎧</span>
                    <span className="hhi-face">💬</span>
                    <span className="hhi-face">✉️</span>
                  </span>
                  Help &amp;{' '}
                  <span className="help-hero-accent">Support Center</span>
                </h1>

                <p className="help-hero-text">
                  Find answers, contact support, and get help with your orders.
                </p>

                <div className="help-hero-stats">
                  <div className="help-stat">
                    <div className="help-stat-value">24/7</div>
                    <div className="help-stat-label">Support</div>
                  </div>

                  <span className="help-stat-sep" />

                  <div className="help-stat">
                    <div className="help-stat-value">&lt; 2 hrs</div>
                    <div className="help-stat-label">Avg Reply</div>
                  </div>

                  <span className="help-stat-sep" />

                  <div className="help-stat">
                    <div className="help-stat-value">4.9★</div>
                    <div className="help-stat-label">Satisfaction</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Container>
      </div>

      {/* Support Categories */}

      <Container className="my-5">
        <div className="text-center mb-5 help-reveal">
          <span className="help-eyebrow">Support Topics</span>

          <h2 className="fw-bold mt-3 mb-2 help-section-title">
            How Can We Help?
          </h2>

          <span className="help-title-bar" />

          <p className="text-muted mt-3 mb-0">
            Browse a topic or jump straight to support below.
          </p>
        </div>

        <Row className="g-4">
          {[
            {
              icon: '📦',
              title: 'Orders',
              text: 'Track orders and delivery updates.',
              color: '#ef6c4d'
            },
            {
              icon: '💳',
              title: 'Payments',
              text: 'Payment failures, refunds and billing.',
              color: '#f97316'
            },
            {
              icon: '🚚',
              title: 'Shipping',
              text: 'Delivery timelines and shipping details.',
              color: '#fb923c'
            },
            {
              icon: '🔄',
              title: 'Returns',
              text: 'Return and replacement support.',
              color: '#f59e0b'
            },
            {
              icon: '🔐',
              title: 'Account',
              text: 'Login, profile and password help.',
              color: '#e8590c'
            },
            {
              icon: '🎟️',
              title: 'Coupons',
              text: 'Offers, discounts and coupon support.',
              color: '#ea580c'
            }
          ].map((cat, i) => (
            <Col
              lg={4}
              md={6}
              key={i}
              className="help-reveal"
              style={{ transitionDelay: `${i * 70}ms` }}
            >
              <Card
                className="border-0 h-100 text-center p-4 help-cat-card"
                style={{ '--cat': cat.color, '--i': i }}
              >
                <div className="help-cat-icon mx-auto">
                  <span className="help-cat-emoji">{cat.icon}</span>
                </div>

                <h4 className="fw-bold mt-3 help-cat-title">{cat.title}</h4>

                <p className="text-muted mb-0">{cat.text}</p>
              </Card>
            </Col>
          ))}
        </Row>
      </Container>

      {/* FAQ */}

      <Container className="my-5">
        <Card className="border-0 shadow-sm help-faq-card help-reveal">
          <Card.Body className="p-3 p-md-4">
            <div className="text-center mb-4">
              <span className="help-eyebrow">FAQ</span>

              <h2 className="fw-bold mt-3 mb-2 help-section-title">
                Frequently Asked Questions
              </h2>

              <span className="help-title-bar" />
            </div>

            <Accordion defaultActiveKey="0">
              <Accordion.Item eventKey="0">
                <Accordion.Header>How can I track my order?</Accordion.Header>

                <Accordion.Body>
                  Go to Orders page and view real-time order updates.
                </Accordion.Body>
              </Accordion.Item>

              <Accordion.Item eventKey="1">
                <Accordion.Header>How do I cancel an order?</Accordion.Header>

                <Accordion.Body>
                  Open the Orders page and click "Cancel Order" on the order,
                  then pick a reason. Cancellation is only possible until the
                  order goes Out For Delivery — after that, wait for delivery
                  and request a refund instead.
                </Accordion.Body>
              </Accordion.Item>

              <Accordion.Item eventKey="2">
                <Accordion.Header>How do I request a refund?</Accordion.Header>

                <Accordion.Body>
                  Visit Orders page and submit a refund request.
                </Accordion.Body>
              </Accordion.Item>

              <Accordion.Item eventKey="3">
                <Accordion.Header>
                  Which payment methods are accepted?
                </Accordion.Header>

                <Accordion.Body>
                  UPI, Debit Card, Credit Card, Net Banking and COD.
                </Accordion.Body>
              </Accordion.Item>
            </Accordion>
          </Card.Body>
        </Card>
      </Container>

      {/* Contact Section */}

      <Container className="my-5">
        <Row>
          <Col lg={4} className="mb-4 help-reveal">
            <Card className="border-0 shadow-sm h-100 help-contact-card">
              <span className="help-blob help-contact-blob" />

              <Card.Body className="position-relative p-4 d-flex flex-column">
                <span className="help-contact-badge">
                  <span className="help-hero-dot" /> Online now
                </span>

                <h3 className="fw-bold mt-3 mb-4">Contact Support</h3>

                <a
                  href="mailto:support@shopease.com"
                  className="help-contact-row"
                >
                  <span className="help-contact-icon">📧</span>
                  <div>
                    <small className="opacity-75 d-block">Email us</small>
                    <span className="fw-semibold">support@shopease.com</span>
                  </div>
                </a>

                <a href="tel:+919876543210" className="help-contact-row">
                  <span className="help-contact-icon">📞</span>
                  <div>
                    <small className="opacity-75 d-block">Call us</small>
                    <span className="fw-semibold">+91 9876543210</span>
                  </div>
                </a>

                <div className="help-contact-row is-static">
                  <span className="help-contact-icon">🕒</span>
                  <div>
                    <small className="opacity-75 d-block">Working hours</small>
                    <span className="fw-semibold">Mon – Sat, 9 AM – 6 PM</span>
                  </div>
                </div>

                <div className="help-contact-promise mt-auto">
                  <div className="help-promise-value">⚡ &lt; 24 hrs</div>
                  <div className="help-promise-label">
                    Average first response
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col lg={8} className="help-reveal" style={{ transitionDelay: '90ms' }}>
            {/* no overflow-hidden here — it would clip the dropdown panel;
                the header rounds its own top corners instead */}
            <Card className="border-0 shadow-sm help-form-card">
              {/* Gradient header strip */}
              <div className="help-form-header d-flex align-items-center gap-3">
                <span className="help-form-header-icon">📝</span>
                <div>
                  <h3 className="fw-bold mb-0">Submit a Support Ticket</h3>
                  <small className="opacity-75">
                    We usually reply within 24 hours
                  </small>
                </div>
              </div>

              <Card.Body className="p-3 p-md-4">
                {/* Live completion meter */}
                {(() => {
                  const filled = filledCount(formData, selectedProduct);
                  const done = filled === 6;

                  return (
                    <div
                      className={`help-progress-wrap${done ? ' is-done' : ''}`}
                    >
                      <div className="help-progress-top">
                        <span className="help-progress-label">
                          Form completion
                        </span>

                        {/* keyed so the encouragement re-animates each step */}
                        <span className="help-progress-note" key={filled}>
                          {PROGRESS_NOTES[filled]}
                        </span>
                      </div>

                      <div className="help-progress-row">
                        <div className="help-progress-track">
                          <div
                            className="help-progress-fill"
                            style={{ width: `${(filled / 6) * 100}%` }}
                          >
                            {filled > 0 && (
                              <span
                                className="help-progress-spark"
                                aria-hidden="true"
                              />
                            )}
                          </div>
                        </div>

                        <span className="help-progress-count" key={filled}>
                          {filled}/6
                        </span>

                        {/* Live checklist — one chip per required field. The
                            first unfilled chip pulses to point the way. */}
                        <span className="help-steps">
                          {(() => {
                            let nextSeen = false;

                            return FIELD_STEPS.map((step, i) => {
                              const on = stepFilled(
                                step,
                                formData,
                                selectedProduct
                              );
                              const isNext = !on && !nextSeen;

                              if (isNext) nextSeen = true;

                              return (
                                <span
                                  key={step.key}
                                  className={`help-step${on ? ' is-on' : ''}${
                                    isNext ? ' is-next' : ''
                                  }`}
                                  style={{ '--i': i }}
                                  title={`${step.label}${on ? ' — done' : ''}`}
                                  aria-label={`${step.label}: ${
                                    on ? 'complete' : 'not filled'
                                  }`}
                                >
                                  <span className="help-step-ico">
                                    {step.icon}
                                  </span>
                                  <span
                                    className="help-step-tick"
                                    aria-hidden="true"
                                  >
                                    ✓
                                  </span>
                                </span>
                              );
                            });
                          })()}
                        </span>
                      </div>
                    </div>
                  );
                })()}

                <Form onSubmit={submitHandler}>
                  <Row className="g-3">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="help-label">
                          👤 Full Name <span className="help-req">*</span>
                        </Form.Label>

                        <Form.Control
                          name="name"
                          value={formData.name}
                          onChange={changeHandler}
                          placeholder="Enter your name"
                          required
                        />
                      </Form.Group>
                    </Col>

                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="help-label">
                          ✉️ Email Address <span className="help-req">*</span>
                        </Form.Label>

                        <Form.Control
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={changeHandler}
                          placeholder="you@example.com"
                          required
                        />
                      </Form.Group>
                    </Col>

                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="help-label">
                          🏷️ Issue Category <span className="help-req">*</span>
                        </Form.Label>

                        <DivineSelect
                          ariaLabel="Issue category"
                          placeholder="Select issue category"
                          value={formData.category}
                          onChange={(next) =>
                            setFormData((prev) => ({
                              ...prev,
                              category: next
                            }))
                          }
                          options={CATEGORY_OPTIONS}
                        />
                      </Form.Group>
                    </Col>

                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="help-label">
                          📦 Product Purchased{' '}
                          <span className="help-req">*</span>
                        </Form.Label>

                        <DivineSelect
                          ariaLabel="Product purchased"
                          placeholder={
                            purchasedProducts.length === 0
                              ? 'No purchased products found'
                              : 'Select product'
                          }
                          emptyText="You have not bought anything yet"
                          value={selectedProduct}
                          onChange={setSelectedProduct}
                          disabled={purchasedProducts.length === 0}
                          options={purchasedProducts.map((item) => ({
                            value: purchaseKey(item),
                            label: item.name,
                            icon: '🛕',
                            /* the order tells two same-named rows apart */
                            meta: `Order #${String(item.orderId).slice(-6)} · ₹${Number(
                              item.price
                            ).toLocaleString()}${
                              item.quantity > 1 ? ` · Qty ${item.quantity}` : ''
                            }`
                          }))}
                        />
                      </Form.Group>
                    </Col>

                    <Col xs={12}>
                      <Form.Group>
                        <Form.Label className="help-label">
                          📌 Subject <span className="help-req">*</span>
                        </Form.Label>

                        <Form.Control
                          name="subject"
                          value={formData.subject}
                          onChange={changeHandler}
                          placeholder="Brief summary of your issue"
                          maxLength={100}
                          required
                        />
                      </Form.Group>
                    </Col>

                    <Col xs={12}>
                      <Form.Group>
                        <div className="d-flex align-items-center justify-content-between">
                          <Form.Label className="help-label mb-0">
                            💬 Message <span className="help-req">*</span>
                          </Form.Label>

                          <small
                            className={`help-counter${
                              formData.message.length > 900 ? ' is-warn' : ''
                            }`}
                          >
                            {formData.message.length}/1000
                          </small>
                        </div>

                        <Form.Control
                          as="textarea"
                          rows={5}
                          name="message"
                          value={formData.message}
                          onChange={changeHandler}
                          placeholder="Describe your issue in detail — include order number, dates and what went wrong."
                          maxLength={1000}
                          required
                          className="mt-2"
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <div className="help-form-footer">
                    <small className="help-privacy">
                      🔒 Your details are kept private and secure.
                    </small>

                    <Button
                      type="submit"
                      size="lg"
                      className="fw-bold px-5 help-submit-btn"
                    >
                      🚀 Submit Ticket
                    </Button>
                  </div>
                </Form>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>

      <style>{helpStyles}</style>
    </>
  );
}

/* The six required fields, in form order — drives the checklist chips beside
   the meter. `value` pulls each one out of the form state. */
const FIELD_STEPS = [
  { key: 'name', icon: '👤', label: 'Full name' },
  { key: 'email', icon: '✉️', label: 'Email address' },
  { key: 'category', icon: '🏷️', label: 'Issue category' },
  { key: 'product', icon: '📦', label: 'Product purchased' },
  { key: 'subject', icon: '📌', label: 'Subject' },
  { key: 'message', icon: '💬', label: 'Message' }
];

const stepFilled = (step, formData = {}, selectedProduct = '') => {
  const value = step.key === 'product' ? selectedProduct : formData[step.key];

  return String(value || '').trim() !== '';
};

/* Encouragement shown beside the meter, indexed by how many fields are done. */
const PROGRESS_NOTES = [
  "Let's get you sorted ✨",
  'Good start 👏',
  'Keep going 🚀',
  'Halfway there ⚡',
  'Almost done 🔥',
  'One field to go 🎯',
  'All set — send it! 🎉'
];

/* How many of the 6 required ticket fields are filled — drives the progress meter. */
const filledCount = (formData = {}, selectedProduct = '') =>
  [
    formData.name,
    formData.email,
    formData.category,
    formData.subject,
    formData.message,
    selectedProduct
  ].filter((value) => String(value || '').trim() !== '').length;

const helpStyles = `
  /* ===== Animated page backdrop ===== */
  .help-bg {
    position: fixed;
    inset: 0;
    z-index: -1;
    overflow: hidden;
    pointer-events: none;
    background:
      radial-gradient(760px 340px at 8% -8%, rgba(124,58,237,0.13), transparent 70%),
      radial-gradient(760px 340px at 92% 108%, rgba(234,88,12,0.15), transparent 70%),
      linear-gradient(180deg, #faf5ff 0%, #fff7ed 55%, #fef3c7 100%);
  }
  /* Second palette crossfading over the base — opacity/transform only, so it
     composites on the GPU instead of repainting the gradient each frame */
  .help-bg-veil {
    position: absolute;
    inset: -20%;
    background:
      radial-gradient(48% 52% at 24% 26%, rgba(251,191,36,0.30), transparent 65%),
      radial-gradient(44% 48% at 78% 30%, rgba(236,72,153,0.22), transparent 65%),
      radial-gradient(54% 58% at 54% 86%, rgba(168,85,247,0.24), transparent 65%);
    opacity: 0.4;
    animation: helpBgVeil 25s ease-in-out infinite alternate;
    will-change: transform, opacity;
  }
  @keyframes helpBgVeil {
    0%   { opacity: 0.28; transform: translate3d(-2%, 2%, 0) scale(1); }
    100% { opacity: 0.66; transform: translate3d(3%, -2%, 0) scale(1.15); }
  }
  .help-bg-blob {
    position: absolute;
    border-radius: 50%;
    filter: blur(64px);
    will-change: transform;
  }
  .help-bg-blob-1 {
    width: 380px; height: 380px;
    top: -110px; left: -80px;
    background: #ea580c;
    opacity: 0.3;
    animation: helpBgDrift 21s ease-in-out infinite alternate;
  }
  .help-bg-blob-2 {
    width: 340px; height: 340px;
    bottom: -120px; right: -70px;
    background: #a855f7;
    opacity: 0.26;
    animation: helpBgDrift 27s ease-in-out infinite alternate-reverse;
  }
  @keyframes helpBgDrift {
    from { transform: translate3d(0, 0, 0) scale(1); }
    to   { transform: translate3d(46px, 38px, 0) scale(1.18); }
  }

  /* Signal arcs broadcasting from the left — a support line in the air */
  .help-bg-signal {
    position: absolute;
    top: 30%;
    left: 10%;
    width: 0; height: 0;
  }
  .help-bg-arc {
    position: absolute;
    top: 0; left: 0;
    width: 240px; height: 240px;
    margin: -120px 0 0 -120px;
    border-radius: 50%;
    border: 1px solid rgba(245, 158, 11, 0.42);
    opacity: 0;
    animation: helpBgArc 10s ease-out infinite;
    will-change: transform, opacity;
  }
  .help-bg-arc:nth-child(2) { animation-delay: -3.3s; }
  .help-bg-arc:nth-child(3) { animation-delay: -6.6s; }
  @keyframes helpBgArc {
    0%   { opacity: 0;    transform: scale(0.2); }
    14%  { opacity: 0.5; }
    100% { opacity: 0;    transform: scale(3.4); }
  }

  .help-bg-spark {
    position: absolute;
    border-radius: 50%;
    background: radial-gradient(circle at 40% 35%, #fffbeb, #fbbf24 60%, rgba(245,158,11,0));
    box-shadow: 0 0 9px rgba(251, 191, 36, 0.85);
    opacity: 0;
    animation-name: helpBgTwinkle;
    animation-timing-function: ease-in-out;
    animation-iteration-count: infinite;
  }
  @keyframes helpBgTwinkle {
    0%, 100% { opacity: 0;    transform: scale(0.4); }
    50%      { opacity: 0.85; transform: scale(1.35); }
  }

  .help-bg-drift {
    position: absolute;
    bottom: -70px;
    line-height: 1;
    opacity: 0;
    filter: drop-shadow(0 6px 14px rgba(180, 83, 9, 0.2));
    animation-name: helpBgRise;
    animation-timing-function: linear;
    animation-iteration-count: infinite;
    will-change: transform, opacity;
  }
  @keyframes helpBgRise {
    0%   { opacity: 0;   transform: translate3d(0, 0, 0) rotate(0deg) scale(0.85); }
    10%  { opacity: 0.5; }
    50%  { transform: translate3d(30px, -55vh, 0) rotate(12deg) scale(1); }
    88%  { opacity: 0.38; }
    100% { opacity: 0;   transform: translate3d(-24px, -115vh, 0) rotate(-10deg) scale(1.1); }
  }

  @media (max-width: 767.98px) {
    .help-bg-signal, .help-bg-drift-extra { display: none; }
  }

  /* ===== Scroll reveal =====
     Declared before the card rules so a card's own :hover transform always
     wins over the settled state here. */
  .help-reveal {
    opacity: 0;
    transform: translateY(26px);
    transition: opacity 0.6s cubic-bezier(0.22, 1, 0.36, 1),
      transform 0.6s cubic-bezier(0.22, 1, 0.36, 1);
  }
  .help-reveal.is-in {
    opacity: 1;
    transform: translateY(0);
  }

  /* ===== Hero =====
     A detached card rather than a full-bleed band, so it reads as its own
     panel with the page backdrop visible around it. */
  .help-hero-wrap {
    padding: clamp(18px, 3vw, 32px) 0 0;
  }
  .help-hero {
    position: relative;
    overflow: hidden;
    isolation: isolate;
    border-radius: 28px;
    padding: clamp(30px, 4.5vw, 52px) clamp(18px, 4vw, 44px);
    border: 1px solid rgba(253, 230, 138, 0.3);
    box-shadow: 0 26px 60px rgba(59, 7, 100, 0.28);
    background:
      radial-gradient(900px 320px at 12% -20%, rgba(251,191,36,0.28), transparent 70%),
      radial-gradient(800px 300px at 88% 120%, rgba(236,72,153,0.20), transparent 70%),
      linear-gradient(120deg, #3b0764 0%, #7c2d12 52%, #ea580c 100%);
    animation: helpHeroIn 0.7s cubic-bezier(0.22, 1, 0.36, 1) both;
  }
  @keyframes helpHeroIn {
    from { opacity: 0; transform: translateY(-18px) scale(0.985); }
    to   { opacity: 1; transform: translateY(0) scale(1); }
  }
  .help-hero-inner { z-index: 3; }

  /* Slow aurora wash inside the card */
  .help-hero-aurora {
    position: absolute;
    inset: -40%;
    z-index: 0;
    pointer-events: none;
    background:
      radial-gradient(36% 44% at 24% 30%, rgba(251, 191, 36, 0.42), transparent 62%),
      radial-gradient(32% 40% at 76% 26%, rgba(236, 72, 153, 0.34), transparent 62%),
      radial-gradient(40% 46% at 58% 82%, rgba(234, 88, 12, 0.40), transparent 62%);
    filter: blur(28px);
    animation: helpHeroAurora 20s ease-in-out infinite alternate;
    will-change: transform;
  }
  @keyframes helpHeroAurora {
    0%   { transform: translate3d(-4%, -3%, 0) rotate(0deg)  scale(1.05); }
    50%  { transform: translate3d(5%, 4%, 0)   rotate(8deg)  scale(1.18); }
    100% { transform: translate3d(-3%, 5%, 0)  rotate(-6deg) scale(1.1); }
  }

  /* Divine gold threads top & bottom — now shimmering */
  .help-hero::before,
  .help-hero::after {
    content: '';
    position: absolute;
    left: 0; right: 0;
    height: 3px;
    z-index: 4;
    background: linear-gradient(90deg, transparent, #fde68a 20%, #f59e0b 50%, #fde68a 80%, transparent);
    background-size: 220% 100%;
    animation: helpThread 3.4s linear infinite;
  }
  .help-hero::before { top: 0; }
  .help-hero::after { bottom: 0; animation-direction: reverse; }
  @keyframes helpThread {
    to { background-position: 220% 0; }
  }

  .help-blob {
    position: absolute;
    border-radius: 50%;
    filter: blur(70px);
    opacity: 0.45;
    pointer-events: none;
  }
  .help-blob-1 {
    width: 300px; height: 300px;
    background: #fbbf24;
    top: -120px; left: 8%;
    animation: helpFloat 9s ease-in-out infinite;
  }
  .help-blob-2 {
    width: 260px; height: 260px;
    background: #f472b6;
    bottom: -130px; right: 6%;
    opacity: 0.35;
    animation: helpFloat 11s ease-in-out infinite reverse;
  }

  /* "We're online" pill */
  .help-hero-badge {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 7px 16px;
    border-radius: 30px;
    font-size: 0.75rem;
    font-weight: 700;
    letter-spacing: 1.2px;
    text-transform: uppercase;
    color: #fde68a;
    background: rgba(255,255,255,0.13);
    border: 1px solid rgba(253,230,138,0.5);
    backdrop-filter: blur(6px);
  }
  .help-hero-dot {
    width: 8px; height: 8px;
    border-radius: 50%;
    background: #4ade80;
    animation: helpPulse 1.8s ease-in-out infinite;
  }
  @keyframes helpPulse {
    0%, 100% { box-shadow: 0 0 0 0 rgba(74,222,128,0.7); }
    50%      { box-shadow: 0 0 0 7px rgba(74,222,128,0); }
  }

  /* "An agent is typing" dots, riding inside the online pill */
  .help-typing {
    display: inline-flex;
    align-items: flex-end;
    gap: 3px;
    margin-left: 4px;
  }
  .help-typing i {
    display: block;
    width: 4px; height: 4px;
    border-radius: 50%;
    background: #fde68a;
    animation: helpTyping 1.4s ease-in-out infinite;
  }
  .help-typing i:nth-child(2) { animation-delay: 0.18s; }
  .help-typing i:nth-child(3) { animation-delay: 0.36s; }
  @keyframes helpTyping {
    0%, 55%, 100% { transform: translateY(0); opacity: 0.4; }
    28%           { transform: translateY(-5px); opacity: 1; }
  }

  /* Voice waveform — the support line, visibly live */
  .help-wave {
    position: absolute;
    left: 0; right: 0; bottom: 0;
    height: clamp(40px, 7vw, 62px);
    display: flex;
    align-items: flex-end;
    gap: 3px;
    padding: 0 10px;
    z-index: 0;
    pointer-events: none;
    opacity: 0.5;
    mask-image: linear-gradient(90deg, transparent, #000 14%, #000 86%, transparent);
    -webkit-mask-image: linear-gradient(90deg, transparent, #000 14%, #000 86%, transparent);
  }
  .help-wave-bar {
    flex: 1 1 0;
    height: 100%;
    border-radius: 4px 4px 0 0;
    transform-origin: bottom center;
    transform: scaleY(0.12);
    background: linear-gradient(180deg, rgba(253,230,138,0.9), rgba(251,191,36,0.08));
    animation-name: helpWave;
    animation-timing-function: ease-in-out;
    animation-iteration-count: infinite;
  }
  @keyframes helpWave {
    0%, 100% { transform: scaleY(0.1); }
    50%      { transform: scaleY(0.72); }
  }

  /* Floating glass icon — its face flips through the support channels */
  .help-hero-icon {
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: clamp(44px, 7vw, 58px);
    height: clamp(44px, 7vw, 58px);
    margin-right: 14px;
    vertical-align: middle;
    border-radius: 50%;
    font-size: clamp(1.4rem, 4vw, 2rem);
    background: rgba(255,255,255,0.16);
    border: 1px solid rgba(253,230,138,0.55);
    box-shadow: 0 8px 22px rgba(0,0,0,0.25);
    perspective: 320px;
    animation: helpFloat 4s ease-in-out infinite;
  }
  .hhi-face {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    backface-visibility: hidden;
    opacity: 0;
    animation: helpFaceFlip 13s ease-in-out infinite;
  }
  .hhi-face:nth-child(1) { animation-delay: 0s; }
  .hhi-face:nth-child(2) { animation-delay: -9.75s; }
  .hhi-face:nth-child(3) { animation-delay: -6.5s; }
  .hhi-face:nth-child(4) { animation-delay: -3.25s; }
  @keyframes helpFaceFlip {
    0%   { opacity: 0; transform: rotateX(90deg); }
    4%   { opacity: 1; transform: rotateX(0deg); }
    21%  { opacity: 1; transform: rotateX(0deg); }
    25%  { opacity: 0; transform: rotateX(-90deg); }
    100% { opacity: 0; transform: rotateX(-90deg); }
  }
  @keyframes helpFloat {
    0%, 100% { transform: translateY(0); }
    50%      { transform: translateY(-9px); }
  }

  .help-hero-title {
    font-size: clamp(1.7rem, 5vw, 2.7rem);
    margin-top: 12px;
    margin-bottom: 0;
    text-shadow: 0 6px 20px rgba(0,0,0,0.25);
  }
  /* Shimmering gold half of the heading */
  .help-hero-accent {
    background: linear-gradient(90deg, #fde68a, #fbbf24 40%, #fde68a);
    background-size: 200% auto;
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
    animation: helpShine 4s linear infinite;
  }
  @keyframes helpShine {
    to { background-position: 200% center; }
  }

  .help-hero-text {
    font-size: clamp(0.95rem, 2.4vw, 1.15rem);
    opacity: 0.9;
    max-width: 620px;
    margin: 10px auto 0;
  }

  /* Trust stats */
  .help-hero-stats {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-wrap: wrap;
    gap: clamp(14px, 4vw, 34px);
    margin-top: 18px;
  }
  .help-stat-value {
    font-size: clamp(1.1rem, 3vw, 1.5rem);
    font-weight: 800;
    color: #fde68a;
    line-height: 1.2;
  }
  .help-stat-label {
    font-size: 0.7rem;
    text-transform: uppercase;
    letter-spacing: 1.2px;
    opacity: 0.8;
  }
  .help-stat-sep {
    width: 1px;
    height: 32px;
    background: rgba(253,230,138,0.35);
  }
  @media (max-width: 575.98px) {
    .help-stat-sep { display: none; }
  }
  @media (prefers-reduced-motion: reduce) {
    .help-hero-icon, .help-blob-1, .help-blob-2,
    .help-hero-accent, .help-hero-dot,
    .help-typing i, .help-wave-bar, .hhi-face { animation: none; }
    .help-wave { display: none; }
    /* Park the flipping icon on its first face */
    .hhi-face { opacity: 0; transform: none; }
    .hhi-face:nth-child(1) { opacity: 1; }
  }
  /* Category cards */
  /* Section heading */
  .help-eyebrow {
    display: inline-block;
    font-size: 0.72rem;
    font-weight: 800;
    letter-spacing: 1.4px;
    text-transform: uppercase;
    color: #b45309;
    background: #fff7ed;
    border: 1px solid #fde68a;
    padding: 5px 14px;
    border-radius: 20px;
  }
  .help-section-title {
    font-size: clamp(1.6rem, 4.5vw, 2.4rem);
    color: #3b0764;
  }
  .help-title-bar {
    display: block;
    width: 70px;
    height: 4px;
    margin: 0 auto;
    border-radius: 4px;
    background: linear-gradient(90deg, #ea580c, #f59e0b);
  }

  /* Category cards */
  .help-cat-card {
    position: relative;
    overflow: hidden;
    border-radius: 20px !important;
    background: linear-gradient(160deg, #ffffff, #fffaf3) !important;
    border: 1px solid #f1e4d0 !important;
    box-shadow: 0 8px 24px rgba(124,45,18,0.06) !important;
    transition: transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease;
    cursor: default;
  }
  /* Accent bar wipes in on hover, tinted by the category colour */
  .help-cat-card::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 4px;
    background: linear-gradient(90deg, var(--cat, #ea580c), #f59e0b);
    transform: scaleX(0);
    transform-origin: left;
    transition: transform 0.35s ease;
  }
  .help-cat-card:hover::before { transform: scaleX(1); }
  .help-cat-card:hover {
    transform: translateY(-10px);
    border-color: rgba(234,88,12,0.4) !important;
    box-shadow: 0 26px 48px rgba(194,65,12,0.20) !important;
  }

  .help-cat-icon {
    width: 78px;
    height: 78px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    font-size: 2rem;
    /* fallback first, then the colour-mixed gradient for modern browsers */
    background: #fff7ed;
    background: linear-gradient(
      135deg,
      color-mix(in srgb, var(--cat, #ea580c) 14%, #ffffff),
      color-mix(in srgb, var(--cat, #ea580c) 30%, #ffffff)
    );
    box-shadow: 0 8px 20px rgba(234,88,12,0.18);
    transition: transform 0.35s ease, box-shadow 0.35s ease;
  }
  .help-cat-card:hover .help-cat-icon {
    transform: scale(1.12) rotate(-8deg);
    box-shadow: 0 14px 30px rgba(234,88,12,0.35);
  }
  /* The emoji bobs on its own layer, so the circle keeps its hover transform */
  .help-cat-emoji {
    display: inline-block;
    animation: helpCatBob 3.4s ease-in-out infinite;
    animation-delay: calc(var(--i, 0) * -0.42s);
  }
  @keyframes helpCatBob {
    0%, 100% { transform: translateY(0) rotate(0deg); }
    50%      { transform: translateY(-6px) rotate(6deg); }
  }
  .help-cat-title {
    color: #3b0764;
    transition: color 0.25s ease;
  }
  .help-cat-card:hover .help-cat-title { color: var(--cat, #ea580c); }

  @media (prefers-reduced-motion: reduce) {
    .help-hero,
    .help-hero-aurora,
    .help-hero::before,
    .help-hero::after,
    .help-cat-emoji,
    .help-progress-note,
    .help-progress-count,
    .help-progress-fill::before,
    .help-progress-spark,
    .help-step.is-on,
    .help-step.is-next,
    .help-progress-wrap.is-done .help-step-ico,
    .help-bg-veil,
    .help-bg-blob,
    .help-bg-arc,
    .help-bg-spark,
    .help-bg-drift { animation: none !important; }
    .help-bg-arc, .help-bg-drift { display: none; }
    .help-bg-spark { opacity: 0.5; }
    .help-reveal {
      opacity: 1;
      transform: none;
      transition: none;
    }
  }

  /* FAQ */
  .help-faq-card {
    border-radius: 24px !important;
    background: linear-gradient(160deg, #ffffff, #fffaf3) !important;
    border: 1px solid #f1e4d0 !important;
    box-shadow: 0 16px 40px rgba(124,45,18,0.08) !important;
  }
  .help-faq-card .accordion { counter-reset: faq; }

  /* Each Q&A is its own rounded card */
  .help-faq-card .accordion-item {
    border: 1px solid #f1e4d0;
    border-radius: 16px !important;
    background: #ffffff;
    margin-bottom: 12px;
    overflow: hidden;
    transition: border-color 0.25s ease, box-shadow 0.25s ease,
      transform 0.25s ease;
  }
  .help-faq-card .accordion-item:hover {
    border-color: rgba(234,88,12,0.35);
    box-shadow: 0 10px 24px rgba(194,65,12,0.10);
    transform: translateY(-2px);
  }
  .help-faq-card .accordion-item:last-child { margin-bottom: 0; }

  .help-faq-card .accordion-button {
    font-weight: 700;
    color: #3b0764;
    background: transparent;
    padding: clamp(14px, 2.5vw, 18px) clamp(14px, 3vw, 20px);
    font-size: clamp(0.95rem, 2.4vw, 1.05rem);
    border-radius: 16px !important;
  }
  .help-faq-card .accordion-button:not(.collapsed) {
    background: linear-gradient(100deg, #fff7ed, #ffffff 75%);
    color: #c2410c;
    box-shadow: none;
  }
  .help-faq-card .accordion-button:focus {
    box-shadow: 0 0 0 0.2rem rgba(234,88,12,0.18);
  }

  /* Auto-numbered badge before each question */
  .help-faq-card .accordion-button::before {
    counter-increment: faq;
    content: counter(faq);
    flex-shrink: 0;
    width: 30px; height: 30px;
    margin-right: 14px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 10px;
    font-size: 0.82rem;
    font-weight: 800;
    color: #b45309;
    background: #fff7ed;
    border: 1px solid #fde68a;
    transition: background 0.25s ease, color 0.25s ease, border-color 0.25s ease;
  }
  .help-faq-card .accordion-button:not(.collapsed)::before {
    background: linear-gradient(135deg, #f59e0b, #ea580c);
    color: #fff;
    border-color: transparent;
  }

  /* Custom saffron chevron in a soft circle */
  .help-faq-card .accordion-button::after {
    flex-shrink: 0;
    width: 30px; height: 30px;
    background-color: #fff7ed;
    background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16' fill='%23ea580c'%3e%3cpath fill-rule='evenodd' d='M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z'/%3e%3c/svg%3e");
    background-size: 14px;
    background-position: center;
    background-repeat: no-repeat;
    border-radius: 50%;
    border: 1px solid #fde68a;
    transition: transform 0.3s ease, background-color 0.25s ease;
  }
  .help-faq-card .accordion-button:not(.collapsed)::after {
    background-color: #fde68a;
  }

  .help-faq-card .accordion-body {
    color: #4b5563;
    line-height: 1.7;
    padding: 0 clamp(14px, 3vw, 20px) clamp(16px, 3vw, 20px)
      clamp(58px, 8vw, 64px);
    font-size: clamp(0.9rem, 2.3vw, 0.98rem);
  }

  @media (prefers-reduced-motion: reduce) {
    .help-faq-card .accordion-item { transition: none; }
  }

  /* Contact card */
  .help-contact-card {
    position: relative;
    border-radius: 24px !important;
    overflow: hidden;
    color: #fff;
    background:
      radial-gradient(500px 220px at 100% 0%, rgba(251,191,36,0.28), transparent 70%),
      linear-gradient(150deg, #3b0764, #7c2d12 55%, #ea580c);
    box-shadow: 0 18px 44px rgba(59,7,100,0.28) !important;
  }
  .help-contact-card::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 3px;
    z-index: 2;
    background: linear-gradient(90deg, transparent, #fde68a 25%, #f59e0b 50%, #fde68a 75%, transparent);
  }
  .help-contact-blob {
    width: 180px; height: 180px;
    background: #fbbf24;
    top: -70px; right: -40px;
    opacity: 0.45;
  }
  .help-contact-icon {
    width: 46px;
    height: 46px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 14px;
    background: rgba(255,255,255,0.18);
    border: 1px solid rgba(253,230,138,0.35);
    font-size: 1.2rem;
  }

  /* Contact card extras */
  .help-contact-badge {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    align-self: flex-start;
    font-size: 0.7rem;
    font-weight: 800;
    letter-spacing: 1.2px;
    text-transform: uppercase;
    color: #fde68a;
    background: rgba(255,255,255,0.13);
    border: 1px solid rgba(253,230,138,0.5);
    padding: 6px 14px;
    border-radius: 30px;
  }
  .help-contact-row {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 10px;
    margin-bottom: 8px;
    border-radius: 14px;
    color: #fff;
    text-decoration: none;
    transition: background 0.2s ease, transform 0.2s ease;
  }
  .help-contact-row:not(.is-static):hover {
    background: rgba(255,255,255,0.12);
    transform: translateX(4px);
    color: #fff;
  }
  .help-contact-promise {
    margin-top: 24px;
    padding: 16px;
    border-radius: 16px;
    text-align: center;
    background: rgba(255,255,255,0.12);
    border: 1px dashed rgba(253,230,138,0.55);
  }
  .help-promise-value {
    font-size: 1.35rem;
    font-weight: 800;
    color: #fde68a;
  }
  .help-promise-label {
    font-size: 0.7rem;
    text-transform: uppercase;
    letter-spacing: 1px;
    opacity: 0.8;
  }

  /* Form */
  .help-form-card {
    border-radius: 24px !important;
    background: linear-gradient(160deg, #ffffff, #fffaf3) !important;
    border: 1px solid #f1e4d0 !important;
    box-shadow: 0 16px 40px rgba(124,45,18,0.08) !important;
  }
  .help-form-header {
    position: relative;
    overflow: hidden;
    /* matches .help-form-card's 24px radius, since the card can no longer
       clip its children */
    border-radius: 24px 24px 0 0;
    padding: 22px 26px;
    color: #fff;
    background:
      radial-gradient(420px 170px at 100% 0%, rgba(251,191,36,0.30), transparent 70%),
      linear-gradient(120deg, #3b0764, #7c2d12 55%, #ea580c);
  }
  .help-form-header-icon {
    width: 52px;
    height: 52px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 14px;
    background: rgba(255,255,255,0.16);
    border: 1px solid rgba(253,230,138,0.5);
    box-shadow: 0 6px 16px rgba(0,0,0,0.2);
    font-size: 1.5rem;
  }
  /* ===== Completion meter =====
     The track is capped at 240px and sits inline with the count, so it reads
     as a compact gauge instead of a full-width bar. */
  .help-progress-wrap {
    margin-bottom: 22px;
  }
  .help-progress-top {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
    margin-bottom: 9px;
  }
  .help-progress-label {
    font-size: 0.72rem;
    font-weight: 800;
    letter-spacing: 1px;
    text-transform: uppercase;
    color: #9a3412;
  }
  /* Rotating encouragement — remounts on every step, so this replays */
  .help-progress-note {
    font-size: 0.74rem;
    font-weight: 700;
    color: #c2410c;
    background: linear-gradient(120deg, #fff7ed, #ffedd5);
    border: 1px solid #fed7aa;
    padding: 3px 12px;
    border-radius: 20px;
    animation: helpNoteIn 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
  }
  @keyframes helpNoteIn {
    0%   { opacity: 0; transform: translateY(9px) scale(0.85); }
    100% { opacity: 1; transform: translateY(0) scale(1); }
  }

  .help-progress-row {
    display: flex;
    align-items: center;
    gap: 12px;
  }
  .help-progress-track {
    position: relative;
    flex: 0 1 240px;
    max-width: 240px;
    height: 10px;
    border-radius: 20px;
    background: #f3e9db;
    overflow: hidden;
    box-shadow: inset 0 1px 3px rgba(124, 45, 18, 0.14);
  }
  .help-progress-fill {
    position: relative;
    height: 100%;
    border-radius: 20px;
    background: linear-gradient(90deg, #f59e0b, #ea580c);
    box-shadow: 0 2px 8px rgba(234,88,12,0.45);
    transition: width 0.5s cubic-bezier(0.22, 1, 0.36, 1), background 0.4s ease;
  }
  /* Barber-pole stripes crawling along the filled portion. The 16px
     background-size and the 16px shift are matched, so the loop is seamless. */
  .help-progress-fill::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 20px;
    background-image: linear-gradient(45deg,
      rgba(255,255,255,0.32) 25%, transparent 25%,
      transparent 50%, rgba(255,255,255,0.32) 50%,
      rgba(255,255,255,0.32) 75%, transparent 75%, transparent);
    background-size: 16px 16px;
    animation: helpStripes 0.85s linear infinite;
  }
  @keyframes helpStripes {
    from { background-position: 0 0; }
    to   { background-position: 16px 0; }
  }
  /* Glowing head riding the leading edge of the fill */
  .help-progress-spark {
    position: absolute;
    right: -2px;
    top: 50%;
    width: 13px;
    height: 13px;
    margin-top: -6.5px;
    border-radius: 50%;
    background: radial-gradient(circle, #fffbeb 0%, #fbbf24 55%, rgba(251,191,36,0));
    box-shadow: 0 0 12px rgba(251, 191, 36, 0.95);
    animation: helpSparkPulse 1.4s ease-in-out infinite;
  }
  @keyframes helpSparkPulse {
    0%, 100% { transform: scale(0.8); opacity: 0.75; }
    50%      { transform: scale(1.25); opacity: 1; }
  }

  .help-progress-count {
    flex: none;
    font-size: 0.75rem;
    font-weight: 800;
    color: #b45309;
    background: #fff7ed;
    border: 1px solid #fde68a;
    padding: 2px 10px;
    border-radius: 20px;
    animation: helpCountPop 0.45s cubic-bezier(0.34, 1.56, 0.64, 1);
  }
  @keyframes helpCountPop {
    0%   { transform: scale(0.6); opacity: 0; }
    100% { transform: scale(1);   opacity: 1; }
  }

  /* ===== Field checklist chips (right of the meter) ===== */
  .help-steps {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-left: auto;
  }
  .help-step {
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    border-radius: 10px;
    font-size: 0.9rem;
    line-height: 1;
    background: #f6efe4;
    border: 1px solid #ece0cd;
    cursor: default;
    transition: background 0.3s ease, border-color 0.3s ease,
      transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.3s ease;
  }
  .help-step-ico {
    filter: grayscale(1);
    opacity: 0.45;
    transition: filter 0.3s ease, opacity 0.3s ease;
  }

  /* Filled — colour returns and a tick badge springs on. Adding .is-on is what
     starts the pop; removing and re-adding the class replays it. */
  .help-step.is-on {
    background: linear-gradient(135deg, #fff7ed, #ffedd5);
    border-color: #fdba74;
    box-shadow: 0 4px 12px rgba(234, 88, 12, 0.2);
    animation: helpStepPop 0.45s cubic-bezier(0.34, 1.56, 0.64, 1);
  }
  .help-step.is-on .help-step-ico {
    filter: none;
    opacity: 1;
  }
  @keyframes helpStepPop {
    0%   { transform: scale(0.7) rotate(-12deg); }
    55%  { transform: scale(1.18) rotate(5deg); }
    100% { transform: scale(1) rotate(0deg); }
  }

  .help-step-tick {
    position: absolute;
    top: -5px;
    right: -5px;
    width: 15px;
    height: 15px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    font-size: 0.6rem;
    font-weight: 900;
    color: #fff;
    background: linear-gradient(135deg, #16a34a, #4ade80);
    box-shadow: 0 2px 6px rgba(22, 163, 74, 0.45);
    opacity: 0;
    transform: scale(0.2) rotate(-90deg);
    transition: opacity 0.28s ease,
      transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
  }
  .help-step.is-on .help-step-tick {
    opacity: 1;
    transform: scale(1) rotate(0deg);
  }

  /* The next field to fill breathes, so there is always somewhere to look */
  .help-step.is-next {
    border-color: #f59e0b;
    animation: helpStepNext 1.9s ease-in-out infinite;
  }
  .help-step.is-next .help-step-ico { opacity: 0.75; }
  @keyframes helpStepNext {
    0%, 100% { box-shadow: 0 0 0 0 rgba(245, 158, 11, 0.5); transform: scale(1); }
    50%      { box-shadow: 0 0 0 6px rgba(245, 158, 11, 0);  transform: scale(1.08); }
  }

  /* Everything turns green the moment all six fields are in */
  .help-progress-wrap.is-done .help-progress-fill {
    background: linear-gradient(90deg, #16a34a, #4ade80);
    box-shadow: 0 2px 12px rgba(22, 163, 74, 0.5);
  }
  .help-progress-wrap.is-done .help-progress-spark {
    background: radial-gradient(circle, #f0fdf4 0%, #4ade80 55%, rgba(74,222,128,0));
    box-shadow: 0 0 14px rgba(74, 222, 128, 0.95);
  }
  .help-progress-wrap.is-done .help-progress-count {
    color: #166534;
    background: #f0fdf4;
    border-color: #86efac;
  }
  .help-progress-wrap.is-done .help-progress-note {
    color: #166534;
    background: linear-gradient(120deg, #f0fdf4, #dcfce7);
    border-color: #86efac;
  }
  .help-progress-wrap.is-done .help-step.is-on {
    background: linear-gradient(135deg, #f0fdf4, #dcfce7);
    border-color: #86efac;
    box-shadow: 0 4px 12px rgba(22, 163, 74, 0.22);
  }
  /* A wave runs across the chips once everything is done. It rides the inner
     icon so it doesn't collide with the chip's own pop animation. */
  .help-progress-wrap.is-done .help-step-ico {
    animation: helpStepWave 1.6s ease-in-out infinite;
    animation-delay: calc(var(--i, 0) * 90ms);
  }
  @keyframes helpStepWave {
    0%, 55%, 100% { transform: translateY(0) rotate(0deg); }
    22%           { transform: translateY(-6px) rotate(-10deg); }
  }

  .help-progress-row { flex-wrap: wrap; }
  @media (max-width: 575.98px) {
    /* checklist drops to its own line rather than squeezing the track */
    .help-steps { margin-left: 0; width: 100%; }
    .help-progress-track { flex: 1 1 auto; }
  }

  .help-label {
    font-size: 0.8rem;
    font-weight: 800;
    color: #7c2d12;
    letter-spacing: 0.3px;
    margin-bottom: 6px;
  }
  .help-req { color: #dc2626; }
  .help-counter {
    font-size: 0.72rem;
    font-weight: 700;
    color: #9ca3af;
  }
  .help-counter.is-warn { color: #ea580c; }

  /* Footer: stacked on mobile, split on desktop */
  .help-form-footer {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 14px;
    margin-top: 22px;
    padding-top: 18px;
    border-top: 1px dashed #eadfce;
  }
  .help-privacy {
    color: #9a3412;
    background: #fff7ed;
    border: 1px solid #fde68a;
    border-radius: 12px;
    padding: 9px 14px;
    text-align: center;
    font-weight: 600;
  }
  @media (min-width: 576px) {
    .help-form-footer {
      flex-direction: row;
      align-items: center;
      justify-content: space-between;
    }
    .help-privacy { text-align: left; }
  }
  .help-form-card .form-control,
  .help-form-card .form-select {
    border-radius: 14px;
    padding: 12px 15px;
    border: 1px solid #eadfce;
    background: #fffdfa;
    transition: border-color 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
  }
  .help-form-card .form-control:focus,
  .help-form-card .form-select:focus {
    border-color: #ea580c;
    background: #ffffff;
    box-shadow: 0 0 0 0.2rem rgba(234,88,12,0.15);
  }
  .help-form-card .form-control::placeholder { color: #b8a894; }

  .help-submit-btn {
    border: none;
    border-radius: 50px;
    padding: 12px 30px;
    font-weight: 800;
    color: #3b0764;
    background: linear-gradient(135deg, #fde68a, #fbbf24 55%, #f59e0b);
    box-shadow: 0 10px 24px rgba(245,158,11,0.35);
    transition: transform 0.22s ease, box-shadow 0.22s ease, filter 0.22s ease;
  }
  .help-submit-btn:hover {
    color: #3b0764;
    transform: translateY(-3px);
    filter: brightness(1.05);
    box-shadow: 0 16px 32px rgba(245,158,11,0.5);
  }
  .help-submit-btn:active { transform: scale(0.98); }

  @media (min-width: 768px) {
    .w-md-auto { width: auto !important; }
  }
`;

export default HelpPage;
